/**
 * React Query hook for video status counts
 * Provides proper caching and optimistic updates for video status counts
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { fetchVideoStatusCounts } from '@/api/videoApi';
import videoKeys from '@/api/querry_keys/videoKeys';

interface VideoStatusCounts {
  approved: number;
  pending: number;
  rejected: number;
  total: number;
  approved_duration_minutes: number;
  pending_duration_minutes: number;
  rejected_duration_minutes: number;
  total_duration_minutes: number;
}

interface UseVideoStatusCountsReturn {
  counts: VideoStatusCounts;
  error: Error | null;
  isLoading: boolean;
  refetch: () => Promise<any>;
  optimisticUpdateCounts: (oldStatus: string | undefined, newStatus: string, duration?: number) => void;
  optimisticDeleteCounts: (status: string, duration?: number) => void;
  optimisticAddCounts: (status: string, duration?: number) => void;
}

interface UseVideoStatusCountsProps {
  userEmail?: string;
}

const defaultCounts: VideoStatusCounts = {
  approved: 0,
  pending: 0,
  rejected: 0,
  total: 0,
  approved_duration_minutes: 0,
  pending_duration_minutes: 0,
  rejected_duration_minutes: 0,
  total_duration_minutes: 0,
};

export const useVideoStatusCounts = ({ 
  userEmail 
}: UseVideoStatusCountsProps = {}): UseVideoStatusCountsReturn => {
  const queryClient = useQueryClient();

  // Create query key that includes userEmail for proper caching
  const queryKey = userEmail 
    ? videoKeys.userStatusCounts(userEmail)
    : videoKeys.statusCounts();

  const {
    data = defaultCounts,
    error,
    isLoading,
    refetch,
  } = useQuery({
    queryKey,
    queryFn: async () => {
      
      try {
        const result = await fetchVideoStatusCounts(userEmail);
        
        // Transform the API response to match our interface
        const counts: VideoStatusCounts = {
          approved: result.approved || 0,
          pending: result.pending || 0,
          rejected: result.rejected || 0,
          total: result.total || 0,
          approved_duration_minutes: result.approved_duration_minutes || 0,
          pending_duration_minutes: result.pending_duration_minutes || 0,
          rejected_duration_minutes: result.rejected_duration_minutes || 0,
          total_duration_minutes: result.total_duration_minutes || 0,
        };
        
        return counts;
      } catch (error) {
        console.error('[VideoStatusCounts] Fetch failed:', error);
        throw error;
      }
    },
    staleTime: 30 * 1000, // Consider data fresh for 30 seconds
    gcTime: 5 * 60 * 1000, // Keep in cache for 5 minutes
  });

  // Optimistic update for status changes
  const optimisticUpdateCounts = useCallback((
    oldStatus: string | undefined, 
    newStatus: string, 
    duration: number = 0
  ) => {
    queryClient.setQueryData<VideoStatusCounts>(queryKey, (prevCounts) => {
      if (!prevCounts) return defaultCounts;
      
      const newCounts = { ...prevCounts };
            
      // Remove from old status if it exists
      if (oldStatus && oldStatus !== 'all') {
        const oldStatusKey = oldStatus as keyof typeof newCounts;
        if (typeof newCounts[oldStatusKey] === 'number') {
          newCounts[oldStatusKey] = Math.max(0, newCounts[oldStatusKey] - 1);
        }
        
        // Update duration for old status
        const oldDurationKey = `${oldStatus}_duration_minutes` as keyof typeof newCounts;
        if (typeof newCounts[oldDurationKey] === 'number') {
          newCounts[oldDurationKey] = Math.max(0, newCounts[oldDurationKey] - duration);
        }
      }
      
      // Add to new status
      if (newStatus && newStatus !== 'all') {
        const newStatusKey = newStatus as keyof typeof newCounts;
        if (typeof newCounts[newStatusKey] === 'number') {
          newCounts[newStatusKey] = newCounts[newStatusKey] + 1;
        }
        
        // Update duration for new status
        const newDurationKey = `${newStatus}_duration_minutes` as keyof typeof newCounts;
        if (typeof newCounts[newDurationKey] === 'number') {
          newCounts[newDurationKey] = newCounts[newDurationKey] + duration;
        }
      }
      
      return newCounts;
    });
  }, [queryClient, queryKey]);

  // Optimistic delete - decreases count for given status
  const optimisticDeleteCounts = useCallback((status: string, duration: number = 0) => {
    queryClient.setQueryData<VideoStatusCounts>(queryKey, (prevCounts) => {
      if (!prevCounts) return defaultCounts;
      
      const newCounts = { ...prevCounts };
      
      if (status && status !== 'all') {
        const statusKey = status as keyof typeof newCounts;
        if (typeof newCounts[statusKey] === 'number') {
          newCounts[statusKey] = Math.max(0, newCounts[statusKey] - 1);
        }
        
        // Update duration
        const durationKey = `${status}_duration_minutes` as keyof typeof newCounts;
        if (typeof newCounts[durationKey] === 'number') {
          newCounts[durationKey] = Math.max(0, newCounts[durationKey] - duration);
        }
      }
      
      // Always decrease total
      newCounts.total = Math.max(0, newCounts.total - 1);
      newCounts.total_duration_minutes = Math.max(0, newCounts.total_duration_minutes - duration);
      
      return newCounts;
    });
  }, [queryClient, queryKey]);

  // Optimistic add - increases count for given status
  const optimisticAddCounts = useCallback((status: string, duration: number = 0) => {
    queryClient.setQueryData<VideoStatusCounts>(queryKey, (prevCounts) => {
      if (!prevCounts) return defaultCounts;
      
      const newCounts = { ...prevCounts };
      
      if (status && status !== 'all') {
        const statusKey = status as keyof typeof newCounts;
        if (typeof newCounts[statusKey] === 'number') {
          newCounts[statusKey] = newCounts[statusKey] + 1;
        }
        
        // Update duration
        const durationKey = `${status}_duration_minutes` as keyof typeof newCounts;
        if (typeof newCounts[durationKey] === 'number') {
          newCounts[durationKey] = newCounts[durationKey] + duration;
        }
      }
      
      // Always increase total
      newCounts.total = newCounts.total + 1;
      newCounts.total_duration_minutes = newCounts.total_duration_minutes + duration;
      
      return newCounts;
    });
  }, [queryClient, queryKey]);

  return {
    counts: data,
    error,
    isLoading,
    refetch,
    optimisticUpdateCounts,
    optimisticDeleteCounts,
    optimisticAddCounts,
  };
};